import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Center,
  Loader,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { IconCamera, IconExclamationCircle, IconId } from "@tabler/icons-react";
import { createBlinkId } from "@microblink/blinkid";
import { DataContext } from "../../../context/DataContext";
import useLocale from "../../../hooks/useLocale";
import { useDeclarePortraitOnly } from "../../../hooks/useOrientation";

// BlinkID in-browser license keys are domain-bound — one key per
// hostname the app is served from. localhost and *.dcams.app are
// production licenses (LIC01000088694, LIC01000088693) that expire
// 2027-10-04; renew them in the Microblink Developer Hub before then.
const LICENSE_KEYS = {
  localhost:
    "sRwCAAlsb2NhbGhvc3QGbGV5SkRjbVZoZEdWa1QyNGlPakUzT1RFeU1ERTRORGczTkRVc0lrTnlaV0YwWldSR2IzSWlPaUl5WXpobU56QmpNeTAwT1RCbUxXVXdNVFF0TkdKaU5TMHlPV1ZqTW1abU5UZGhOVGdpZlE9PVU0LBuNSeDq7FzAfuHlZz4P6/rry7sR0NhMGElYAR8YlAZ4ZsGrSbPokYBgyGDWH/k/yLMG50f4kWdZq35VkApctbDeBRvIWcHtXu/yXYA8LjX07embBpN7CL4ElWPkhpSvmgWr4fyZsua+gtW93fSmaBlYOzbgEsxtOA==",
  // Exact-domain key for the current ngrok tunnel subdomain.
  "76bd9a9bd5d0.ngrok.app":
    "sRwCABY3NmJkOWE5YmQ1ZDAubmdyb2suYXBwBmxleUpEY21WaGRHVmtUMjRpT2pFM09EWXpOell4TWpjeU16QXNJa055WldGMFpXUkdiM0lpT2lJeVl6aG1OekJqTXkwME9UQm1MV1V3TVRRdE5HSmlOUzB5T1dWak1tWm1OVGRoTlRnaWZRPT3CTJO/oSoOp1p5yE6J/grE5FaqQ2hEO4pfEGcqkbnJk4sIVCjsqhBRMNyjJchrh0JrDN+Efyx6/0Mh+SxrwWNENsmwD0rnY6Nx4xzegW6saIbPWGR1cnTjaAUPc0Mt",
  // Wildcard key for *.dcams.app — preview (preview-fe.dcams.app) and
  // production frontend domains.
  "*.dcams.app":
    "sRwCAAsqLmRjYW1zLmFwcAZsZXlKRGNtVmhkR1ZrVDI0aU9qRTNPVEV5TURFM056WTRPVFlzSWtOeVpXRjBaV1JHYjNJaU9pSXlZemhtTnpCak15MDBPVEJtTFdVd01UUXROR0ppTlMweU9XVmpNbVptTlRkaE5UZ2lmUT0982ka99TsyyjxoLwtk3rXkVNzEbCA3sjdHc7ny5wYZZP4CFwixfz0CO1Cxl1N45LFJ2GdB2iDPYtMq32pJTjnBVoBxQoTUk1bhGZx0sJNvmsmiptPdC2FxTTSfffO0dxKhQ/p46HYnBC5DujlDkw/Y9nLMxE9oL1U4Rzt",
  // Wildcard key for *.ngrok.app — phone testing through the ngrok tunnel.
  "*.ngrok.app":
    "sRwCAAsqLm5ncm9rLmFwcAZsZXlKRGNtVmhkR1ZrVDI0aU9qRTNPRFl6TnpVMU1qWTFNemdzSWtOeVpXRjBaV1JHYjNJaU9pSXlZemhtTnpCak15MDBPVEJtTFdVd01UUXROR0ppTlMweU9XVmpNbVptTlRkaE5UZ2lmUT09fVNLuAmjhc870zzYBl6ASKeVKySMP4K7hsRSFS+o8X2egCSmZbEuji+e1N7yLEn+toPD0xcQrs++Jh5TDPgz8zqgia+JfqQGluEaMeAyQub9cjkKDz8h2cEznx0rqg==",
};

const getLicenseKey = () => {
  const { hostname } = window.location;
  if (LICENSE_KEYS[hostname]) return LICENSE_KEYS[hostname];
  const wildcard = Object.keys(LICENSE_KEYS).find(
    (domain) =>
      domain.startsWith("*.") && hostname.endsWith(domain.slice(1))
  );
  return wildcard ? LICENSE_KEYS[wildcard] : undefined;
};

// BlinkID StringResult fields are keyed by alphabet ({ latin, arabic,
// cyrillic, greek }), each holding { value }. Collapse to a plain string.
const str = (field) => {
  if (!field) return "";
  if (typeof field === "string") return field;
  const alphabet = field.latin ?? field.cyrillic ?? field.arabic ?? field.greek;
  if (!alphabet) return "";
  return typeof alphabet === "string" ? alphabet : alphabet.value ?? "";
};

// DateResult → "MM/DD/YYYY", falling back to the raw scanned string.
const dateStr = (field) => {
  if (!field) return "";
  const { day, month, year } = field;
  if (day && month && year) {
    return `${String(month).padStart(2, "0")}/${String(day).padStart(2, "0")}/${year}`;
  }
  return str(field.originalString);
};

// DateResult → "YYYYMMDD", the format the verification forms and
// backend expect for dob.
const dateYmd = (field) => {
  if (!field?.year || !field?.month || !field?.day) return "";
  return `${field.year}${String(field.month).padStart(2, "0")}${String(field.day).padStart(2, "0")}`;
};

// Pull the fields the datamatch flow cares about out of a
// BlinkIdScanningResult. Prefers the parsed PDF417 barcode sub-result
// (plain strings + structured AAMVA address) and falls back to the
// top-level VIZ fields. Keys match the context `data` shape the
// verification forms prefill from (fn/ln/dob/addr/city/state/zip).
const classValue = (v) =>
  typeof v === "string" ? v : v?.id ?? v?.rawValue ?? "";

export const extractDocumentFields = (result) => {
  const barcode = result?.subResults?.find((sr) => sr?.barcode?.parsed)?.barcode;
  const addrInfo = barcode?.addressDetailedInfo;
  return {
    fn: barcode?.firstName || str(result?.firstName),
    ln: barcode?.lastName || str(result?.lastName),
    fullName: barcode?.fullName || str(result?.fullName),
    dob: dateYmd(result?.dateOfBirth) || dateYmd(barcode?.dateOfBirth),
    addr: addrInfo?.street || "",
    city: addrInfo?.city || "",
    state: addrInfo?.jurisdiction || "",
    zip: (addrInfo?.postalCode || "").slice(0, 5),
    address: barcode?.address || str(result?.address),
    dateOfBirth: dateStr(result?.dateOfBirth),
    dateOfExpiry: dateStr(result?.dateOfExpiry),
    documentNumber: barcode?.documentNumber || str(result?.documentNumber),
    // BlinkID 8001+ wraps these as { id?, rawValue } and renamed
    // type -> documentType; prefer the typed id, fall back to the raw token.
    documentType: classValue(result?.documentClassInfo?.documentType),
    documentCountry: classValue(result?.documentClassInfo?.country),
    documentRegion: classValue(result?.documentClassInfo?.region),
  };
};

// Do the extracted fields satisfy the same rules the datamatch form
// validates (fn/ln length, YYYYMMDD dob, full address with 5-digit
// zip)? Guardrail for flows where the user is never allowed to type:
// when this fails, the datamatch would either auto-submit garbage or
// fall back to an editable form — both wrong for barcode-data-only
// routes, so the caller fails (or falls back) instead.
export const hasUsableIdentityFields = (fields) => {
  const dobPattern = /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/;
  return (
    (fields?.fn || "").trim().length >= 2 &&
    (fields?.ln || "").trim().length >= 2 &&
    dobPattern.test(fields?.dob || "") &&
    Boolean(fields?.addr) &&
    Boolean(fields?.city) &&
    Boolean(fields?.state) &&
    (fields?.zip || "").trim().length === 5
  );
};

// Map the route step's declarative scan_sides setting to BlinkID session
// settings. "back_only" runs a single-side scan with mandatory barcode
// and MRZ/VIZ disabled, which switches the SDK's UX into its
// "document-with-barcode" mode ("scan the barcode side" guidance) — the
// PDF417 on US licenses carries the full identity payload.
const buildScanConfig = (routeSettings) => {
  switch (routeSettings?.scan_sides) {
    case "back_only":
      return {
        scanningMode: "single",
        scanningSettings: {
          barcodeModule: { presenceMandatory: true },
          mrzModule: null,
          vizModule: null,
        },
      };
    case "front_only":
      return { scanningMode: "single" };
    default:
      // "front_and_back" — SDK default automatic multi-side flow
      return {};
  }
};

// The SDK's feedback UI opens an onboarding modal ("Locate barcode on
// the document" + a "Start scanning" button) over the camera before the
// scanner starts. Our own intro step already tells the user where the
// barcode is, so that modal is a redundant extra tap — it is off unless
// the route step opts back in with show_onboarding_guide: true.
const buildFeedbackUiOptions = (routeSettings) => ({
  showOnboardingGuide: routeSettings?.show_onboarding_guide === true,
});

// `initialState` seeds the UI state ({ isLoading, errorMessage,
// extracted }) — Storybook-only, so each visual state can render
// without driving the real SDK. Production callers omit it.
export default function BlinkIdInBrowser({ onComplete, onStart, initialState = {} }) {
  // Not laid out for landscape: keeps the rotate prompt up here even
  // under a landscape preview flag (see OrientationWarning).
  useDeclarePortraitOnly();
  const { settings } = useContext(DataContext);

  // Step-level settings from the journey route (dcams_routes.route JSONB
  // in the DB), so scanning behavior is controlled per-route without a
  // frontend deploy.
  const routeSettings =
    settings?.journey?.route?.find((r) => r.path?.includes("blinkid"))
      ?.settings || null;
  // Barcode-only steps get the barcode-first CTA from the v2 flow
  // design; multi-side scans keep the generic label.
  const barcodeMode = routeSettings?.scan_sides === "back_only";
  const docLocale = useLocale("document-capture");
  const [isLoading, setIsLoading] = useState(initialState.isLoading ?? false);
  const [errorMessage, setErrorMessage] = useState(initialState.errorMessage ?? "");
  const [extracted, setExtracted] = useState(initialState.extracted ?? null);
  const blinkIdRef = useRef(null);
  const unsubscribersRef = useRef([]);

  const destroySdk = async () => {
    unsubscribersRef.current.forEach((unsubscribe) => unsubscribe?.());
    unsubscribersRef.current = [];
    const instance = blinkIdRef.current;
    blinkIdRef.current = null;
    if (instance) {
      try {
        await instance.destroy();
      } catch (error) {
        console.warn("[BlinkID] destroy failed:", error);
      }
    }
  };

  useEffect(() => {
    return () => {
      destroySdk();
    };
  }, []);

  // Hide the license-driven "Powered by Microblink" watermark. There is
  // no SDK option for it (it's flagged on by demo/trial licenses at
  // unlock time, and the flag is read once at UI mount, inside
  // createBlinkId — too early for us to flip it on the instance).
  // The SDK injects its stylesheet from this global string when the
  // feedback UI mounts, so appending a rule here lands inside the SDK's
  // own style scope. Targets the demo (top-[15%]) and production
  // (bottom-4) logo wrappers' SVGs only.
  const hideMicroblinkWatermark = () => {
    const marker = "/*mb-watermark-off*/";
    const css =
      marker +
      'div.absolute.flex.justify-center.items-center.w-full > svg[aria-hidden="true"]{display:none !important;}';
    if (
      typeof window.__blinkidUxManagerCssCode === "string" &&
      !window.__blinkidUxManagerCssCode.includes(marker)
    ) {
      window.__blinkidUxManagerCssCode += css;
    }
  };

  const startScan = async () => {
    setIsLoading(true);
    setErrorMessage("");
    setExtracted(null);
    hideMicroblinkWatermark();
    const licenseKey = getLicenseKey();
    if (!licenseKey) {
      console.error(
        `[BlinkID] no license key registered for hostname "${window.location.hostname}"`
      );
      setErrorMessage(
        "The document scanner is not licensed for this domain."
      );
      setIsLoading(false);
      return;
    }
    try {
      const blinkid = await createBlinkId({
        licenseKey,
        // The SDK resolves "resources/..." against this base URL, so
        // the trailing slash is required — without it the /blinkid
        // segment is dropped and requests hit /resources instead.
        resourcesLocation: `${window.location.origin}/blinkid/`,
        userId: settings?.request_id || undefined,
        feedbackUiOptions: buildFeedbackUiOptions(routeSettings),
        ...buildScanConfig(routeSettings),
      });
      blinkIdRef.current = blinkid;

      unsubscribersRef.current.push(
        blinkid.addOnResultCallback((result) => {
          console.log("[BlinkID] scan result:", result);
          const fields = extractDocumentFields(result);
          console.log("[BlinkID] extracted fields:", fields);
          setExtracted(fields);
          destroySdk();
          onComplete?.({ fields, raw: result });
        })
      );

      unsubscribersRef.current.push(
        blinkid.addOnErrorCallback((errorState) => {
          console.error("[BlinkID] processing error:", errorState);
          // Timeouts and unsupported documents surface the SDK's own
          // retry modal, which resets the scanning session itself —
          // destroying here races the worker and throws a BindingError
          // on the deleted session. Only tear down on fatal errors.
          const sdkHandlesRetry =
            errorState === "inactivity_timeout" ||
            errorState === "scan_step_timeout" ||
            errorState === "unsupported_document";
          if (!sdkHandlesRetry) {
            setErrorMessage(
              "Something went wrong while scanning your document. Please try again."
            );
            destroySdk();
          }
        })
      );

      // User closed the camera overlay without finishing the scan —
      // tear down so a fresh session starts on the next attempt.
      unsubscribersRef.current.push(
        blinkid.cameraUi.addOnDismountCallback(() => {
          if (blinkIdRef.current) {
            destroySdk();
          }
        })
      );

      onStart?.();
    } catch (error) {
      console.error("[BlinkID] initialization failed:", error);
      setErrorMessage(
        "The document scanner could not be started. Please check your camera permissions and try again."
      );
      destroySdk();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box p="md">
      <Stack spacing="md">
        {errorMessage && (
          <Alert
            icon={<IconExclamationCircle size={18} />}
            color="red"
            variant="light"
          >
            {errorMessage}
          </Alert>
        )}

        {extracted ? (
          <Paper withBorder p="md" radius="md">
            <Stack spacing={4}>
              <Text fw={600}>Document scanned</Text>
              {Object.entries(extracted)
                .filter(([, value]) => value)
                .map(([key, value]) => (
                  <Text key={key} fz="sm">
                    <Text span c="dimmed">
                      {key}:
                    </Text>{" "}
                    {String(value)}
                  </Text>
                ))}
            </Stack>
          </Paper>
        ) : isLoading ? (
          <Center py="xl">
            <Stack align="center" spacing="xs">
              <Loader />
              <Text fz="sm" c="dimmed">
                Starting document scanner…
              </Text>
            </Stack>
          </Center>
        ) : (
          <Button
            size="lg"
            fullWidth
            leftIcon={
              barcodeMode ? <IconCamera size={20} /> : <IconId size={20} />
            }
            onClick={startScan}
          >
            {barcodeMode
              ? docLocale?.barcodeIntro?.scanButton || "Scan My Barcode"
              : "Scan your ID"}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
